import { useEffect } from "react";
import { captureException } from "@sentry/nextjs";
import { add, endOfDay, startOfToday } from "date-fns";
import isEmpty from "lodash/isEmpty";
import startCase from "lodash/startCase";
import { useRouter } from "next/router";

import { analytics } from "@flare/analytics";
import { useSession } from "@flare/next-auth/react";

import type { PracticeArea } from "@/edge/contract";
import { useLssSlots } from "@/hooks/lss/use-lss-slots";
import { useLssSlotsByRequestId } from "@/hooks/lss/use-lss-slots-by-request-id";
import { funnelInputsNameMap } from "@/modules/v2/funnel/config";
import { getFunnelMeta } from "@/modules/v2/funnel/funnel.utils";
import { useFunnelAnswers } from "@/modules/v2/funnel/hooks/useFunnelAnswers";
import { BiEventsNames } from "@/services/analytics/bi-event-names";

import { meetingType } from "../funnel.consts";
import { Scheduler } from "../Scheduler/Scheduler";
import { getTimeZoneAbbreviation } from "../Scheduler/Scheduler.utils";

export function LssScheduler() {
  const session = useSession();
  const { query } = useRouter();
  const { funnelAnswers, setFunnelAnswers } = useFunnelAnswers();
  const { competencies = [] } = getFunnelMeta();
  const bookitRequestId = funnelAnswers.bookitRequestId || "";
  const practice = funnelAnswers.practice || (query.practice as string);

  const practiceArea = startCase(practice) as PracticeArea;
  const isValidPractice = ["Family", "Immigration"].includes(practiceArea);

  const isReadyToFetchSlots = isValidPractice && !isEmpty(funnelAnswers);
  const shouldFetchSlotsByRequestId =
    isReadyToFetchSlots && Boolean(bookitRequestId);

  const {
    data: publicSlots,
    isPending: isPendingPublicSlots,
    error: publicSlotsError,
  } = useLssSlots({
    enabled: isReadyToFetchSlots && !shouldFetchSlotsByRequestId,
    userProps: {
      practiceArea,
      competencies,
      subPractice: funnelAnswers.subPractice,
      serviceType: funnelAnswers.serviceType,
      sponsorType: funnelAnswers.sponsorType,
      stateCode: funnelAnswers.stateCode,
      county: funnelAnswers.county,
      email: funnelAnswers.email,
    },
  });

  const now = new Date();
  const {
    isPending: isPendingSlotsByRequestId,
    data: slotsByBookitId,
    error: slotsByBookitIdError,
  } = useLssSlotsByRequestId({
    from: startOfToday(),
    to: endOfDay(add(now, { days: 7 })),
    bookitRequestId,
  });

  // shouldFetchSlotsByRequestId is used here because useQuery's isPending can be true even if the query is disabled
  const isLoading =
    (shouldFetchSlotsByRequestId && isPendingSlotsByRequestId) ||
    (!shouldFetchSlotsByRequestId && isPendingPublicSlots);
  const error = slotsByBookitIdError || publicSlotsError;
  const slots = slotsByBookitId || publicSlots || {};
  const hasSlots = !isEmpty(slots);

  useEffect(() => {
    // This bi event is relevant only to the public slots endpoint because the other one doesn't require these parameters
    const isMissingCounty = !funnelAnswers.county;
    const isMissingStateCode = !funnelAnswers.stateCode;
    const isMissingPractice = !funnelAnswers.practice;
    const isMissingSubPractice = !funnelAnswers.subPractice;

    const isMissingARequiredParameter =
      isMissingCounty ||
      isMissingStateCode ||
      isMissingPractice ||
      isMissingSubPractice;

    if (
      isReadyToFetchSlots &&
      !shouldFetchSlotsByRequestId &&
      isMissingARequiredParameter
    ) {
      analytics.track(BiEventsNames.WebPageError, {
        error_type: "missing_county_or_state_code",
        practice: funnelAnswers.practice,
        flare_id: session?.data?.user?.id || null,
        error_message: "missing_county_or_state_code",
        county: funnelAnswers.county,
        state_code: funnelAnswers.stateCode,
        sub_practice: funnelAnswers.subPractice,
      });
    }
  }, [
    funnelAnswers.county,
    funnelAnswers.stateCode,
    funnelAnswers.practice,
    funnelAnswers.subPractice,
    isReadyToFetchSlots,
    session?.data?.user?.id,
    shouldFetchSlotsByRequestId,
  ]);

  useEffect(() => {
    if (!hasSlots) return;

    const clientTimeZone = getTimeZoneAbbreviation() || "";

    const [firstAvailableDate] = Object.keys(slots);

    analytics.track(BiEventsNames.WebCalendarPageView, {
      range_start: slots[firstAvailableDate][0],
      number_of_fetched_days: Object.keys(slots).length,
      practice: practiceArea?.toLowerCase(),
      first_presented_time_slot: slots[firstAvailableDate][0],
      number_of_time_slots: slots[firstAvailableDate].length,
      current_step_key: query.step,
      meeting_type: meetingType.lss,
    });

    setFunnelAnswers((prevAnswers) => ({
      ...prevAnswers,
      timezone: clientTimeZone,
      meetingType: meetingType.lss,
    }));
  }, [hasSlots, practiceArea, query.step, setFunnelAnswers]);

  useEffect(() => {
    if (error) {
      analytics.track(BiEventsNames.WebFunnelError, {
        error_type: "get_lss_slots_error",
        error_message: error.message,
        practice: funnelAnswers.practice,
        flare_id: session?.data?.user?.id || null,
        is_by_bookit_request: shouldFetchSlotsByRequestId,
      });
      captureException(error, {
        extra: {
          action: "lssMeetingSlots",
        },
      });
    }
  }, [
    error,
    funnelAnswers.practice,
    session?.data?.user?.id,
    shouldFetchSlotsByRequestId,
  ]);

  function onDayChange(selectedDay: string) {
    if (!hasSlots) return;

    const selectedDayIndex = Object.keys(slots).indexOf(selectedDay);

    const timeSlots = slots[selectedDay];
    analytics.track(BiEventsNames.WebCalendarClick, {
      first_presented_time_slot: timeSlots[0].start.toISOString(),
      number_of_time_slots: timeSlots.length,
      date_selected: selectedDay,
      date_slot_index: selectedDayIndex,
      number_of_fetched_days: Object.keys(slots).length,
      meeting_type: meetingType.lss,
    });
  }

  // TODO - isFetched && !hasSlots should show a message to the user
  return (
    <Scheduler
      onDayChange={onDayChange}
      getDefaultSelectedDay={(slots) => Object.keys(slots ?? [])[0]}
      loading={isLoading}
      slots={slots}
      name={funnelInputsNameMap.lssStartTime}
    />
  );
}
